/* Copyright (c) 2020-present CloudIO Inc */

import { DBType, IoConnections } from '@cloudio-saas/datasource-types';
import { useStateXAtomValue } from '@cloudio-saas/statex';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Icon from 'icons/Icon';
import { sidebarActiveConnectionUidAtom } from 'layout/Sidebar/atoms';
import React, { useEffect, useState } from 'react';
import { isFromDB } from 'store/storeTypes';
import { useStoreBusy, useStoreCurrentRecord } from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import CodeInput from '../../../../custom/CodeInput';
import useComponentSize from '../../../../hooks/useComponentSize';
import { useTheme } from '../../../../theme/Theme';
import { useBackendCodeEditorTypes } from '../DataSourceView/CodeEditorTypes';
import { usePending, usePlatform } from 'platform/PlatformHooks';
import WaveDots from 'utils/WaveDots';
import Store from 'store/Store';
import Checkbox from '../fields/Checkbox';

const useStyles = makeStyles(() => ({
  root: {
    padding: '0 20px',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
  },
  form: {
    flexBasis: 'auto',
    flex: 0,
    '& > *': {
      marginTop: 20,
    },
  },
  editor: {
    flex: 1,
    display: 'block',
    marginBottom: 20,
  },
  header: {
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
  },
  heading: {
    flex: 1,
  },
}));

type FullDBType = DBType | 'Oracle';

// const DEFAULT_PORTS: Record<FullDBType, string> = {
//   MySQL: '3306',
//   Postgres: '5432',
//   MSSQL: '1433',
//   Oracle: '1521',
// };
interface ConnectionDetails {
  username: string;
  password: string;
  host: string;
  port: string;
  dbname: string;
  otherParams: string;
}

const DB_TYPE_HELPER_TEXT: Record<FullDBType, string> = {
  MySQL: 'e.g., inactive_connection_ttl=300&pool_min=6&pool_max=40',
  Postgres:
    'e.g., pool_min=6 pool_max=100 sslmode=require sslcert=certs/client-cert.pem sslkey=certs/client-key.pem sslrootcert=certs/server-ca.pem',
  MSSQL: 'e.g., IntegratedSecurity=true;TrustServerCertificate=true',
  Oracle: 'e.g., pool_min=6&pool_max=100',
};

export function parseDatabaseUrl(
  dbType: FullDBType,
  connectionUrl: string,
): ConnectionDetails {
  if (!connectionUrl) {
    return {
      username: '',
      password: '',
      host: '',
      port: '',
      dbname: '',
      otherParams: '',
    };
  }
  switch (dbType) {
    case 'MySQL':
    case 'Oracle': {
      const stripped = connectionUrl.replace(
        new RegExp(`^${dbType.toLowerCase()}://`, 'i'),
        '',
      );
      const [authAndHost, dbAndParams] = stripped.split('/', 2);
      const [authPart, hostPart] = authAndHost.split(/@(?=[^@]*$)/);
      const [username, password] = authPart.split(':');
      const [host, port] = hostPart.split(':');
      const [dbname, queryParams] = dbAndParams?.split('?');
      return {
        username: username || '',
        password: password || '',
        host: host || '',
        // port: port || DEFAULT_PORTS[dbType],
        port: port || '',
        dbname: dbname || '',
        otherParams: queryParams || '',
      };
    }
    case 'Postgres': {
      const params = connectionUrl.split(' ').reduce((acc, param) => {
        const [key, value] = param.split('=');
        if (key && value) acc[key] = value;
        return acc;
      }, {} as Record<string, string>);

      const otherParams = Object.entries(params)
        .filter(
          ([key]) =>
            !['user', 'password', 'host', 'port', 'dbname'].includes(key),
        )
        .map(([key, value]) => `${key}=${value}`)
        .join(' ');

      return {
        username: params.user || '',
        password: params.password || '',
        host: params.host || '',
        // port: params.port || DEFAULT_PORTS.Postgres,
        port: params.port || '',
        dbname: params.dbname || '',
        otherParams,
      };
    }
    case 'MSSQL': {
      const params = connectionUrl.split(';').reduce((acc, param) => {
        const [key, value] = param.split('=');
        if (key && value) acc[key] = value;
        return acc;
      }, {} as Record<string, string>);
      const otherParams = Object.entries(params)
        .filter(([key]) => !['uid', 'pwd', 'server', 'database'].includes(key))
        .map(([key, value]) => `${key}=${value}`)
        .join(';');
      const [host, port] = params.server?.replace('tcp:', '')?.split(',') || [];
      return {
        username: params.uid || '',
        password: params.pwd || '',
        host: host || '',
        port: port || '',
        // port: port || DEFAULT_PORTS.MSSQL,
        dbname: params.database || '',
        otherParams,
      };
    }
    default:
      return {
        username: '',
        password: '',
        host: '',
        port: '',
        dbname: '',
        otherParams: '',
      };
  }
}

export function buildDatabaseUrl(
  dbType: FullDBType,
  details: ConnectionDetails,
): string {
  function buildUrl(type: 'MySQL' | 'Oracle'): string {
    const { username, password, host, port, dbname, otherParams } = details;
    const handleOtherParams = (rawParams?: string): string => {
      if (!rawParams) return '';
      const pairs = rawParams
        .split('&')
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => {
          const [key, value] = p.split('=');
          return key && value !== undefined ? `${key}=${value}` : '';
        })
        .filter(Boolean);
      return pairs.length ? `?${pairs.join('&')}` : '';
    };
    const _otherParms = handleOtherParams(otherParams);
    return `${type.toLowerCase()}://${username}:${password}@${host}:${
      // port || DEFAULT_PORTS[type]
      port || ''
    }/${dbname}${_otherParms}`;
  }

  const builders: Record<FullDBType, () => string> = {
    MySQL: () => buildUrl('MySQL'),
    Oracle: () => buildUrl('Oracle'),
    Postgres: () => {
      const baseParams = [
        `host=${details.host}`,
        `port=${details.port || ''}`,
        // `port=${details.port || DEFAULT_PORTS['Postgres']}`,
        `dbname=${details.dbname}`,
        `user=${details.username}`,
        `password=${details.password}`,
      ];
      const otherParams = details.otherParams
        ? details.otherParams?.trim()?.split(' ')
        : [];
      return [...baseParams, ...otherParams].join(' ');
    },
    MSSQL: () => {
      const baseParams = [
        // `server=tcp:${details.host},${details.port || DEFAULT_PORTS['MSSQL']}`,
        `server=tcp:${details.host},${details.port || ''}`,
        `uid=${details.username}`,
        `pwd=${details.password}`,
        `database=${details.dbname}`,
      ];
      const otherParams = details.otherParams
        ? details.otherParams?.trim()?.split(';')
        : [];
      return [...baseParams, ...otherParams].join(';');
    },
  };
  return builders[dbType]();
}

export function maskPasswordInUrl(
  dbType: FullDBType,
  connectionUrl: string,
): string {
  const maskUsingURL = () => {
    try {
      const u = new URL(connectionUrl);
      if (u.password) {
        u.password = '*'.repeat(u.password.length);
        return u.toString();
      }
      return connectionUrl;
    } catch {
      return connectionUrl;
    }
  };
  const maskers: Record<FullDBType, () => string> = {
    MySQL: maskUsingURL,
    Oracle: maskUsingURL,
    Postgres: () =>
      connectionUrl.replace(
        /password=([^ ]+)/i,
        (_, password) => `password=${'*'.repeat(password.length)}`,
      ),
    MSSQL: () =>
      connectionUrl.replace(
        /pwd=([^;]+)/i,
        (_, password) => `pwd=${'*'.repeat(password.length)}`,
      ),
  };
  return maskers[dbType]();
}

export function isPasswordMasked(
  dbType: FullDBType,
  connectionUrl: string,
): boolean {
  const checkUsingURL = () => {
    try {
      const u = new URL(connectionUrl);
      return u.password !== '' && /^\*+$/.test(u.password);
    } catch {
      return false;
    }
  };
  const checkers: Record<FullDBType, () => boolean> = {
    MySQL: checkUsingURL,
    Oracle: checkUsingURL,
    Postgres: () => {
      const passwordMatch = connectionUrl.match(/password=([^ ]+)/i);
      return passwordMatch ? /^\*+$/.test(passwordMatch[1]) : false;
    },
    MSSQL: () => {
      const passwordMatch = connectionUrl.match(/pwd=([^;]+)/i);
      return passwordMatch ? /^\*+$/.test(passwordMatch[1]) : false;
    },
  };

  return checkers[dbType]();
}

function validateUrl(dbType: FullDBType, url: string): boolean {
  if (!url) return false;
  const lowered = url.toLowerCase();
  if (dbType === 'MySQL') return lowered.startsWith('mysql://');
  if (dbType === 'Oracle') return lowered.startsWith('oracle://');
  if (dbType === 'Postgres')
    return lowered.includes('user=') && lowered.includes('host=');
  if (dbType === 'MSSQL')
    return lowered.includes('server=') && lowered.includes('uid=');

  return false;
}

export const ConnectionEditBody = ({
  appUid,
  store,
  width,
}: {
  appUid: string;
  store: Store<IoConnections>;
  width?: number;
}): React.ReactElement => {
  const theme = useTheme();
  const classes = useStyles();
  const s = useComponentSize();
  // const store = useDataStore<IoConnections>({
  //   appUid,
  //   pageId: '$$Sidebar$$',
  //   ds: 'IoConnections',
  //   alias: 'connectionEditAlias',
  // });
  // const activeConnectionId = useStateXAtomValue(sidebarActiveConnectionUidAtom);
  // if (activeConnectionId) {
  //   store.read({
  //     props: {
  //       filter: [
  //         {
  //           connectionUid: { is: activeConnectionId },
  //         },
  //       ],
  //       limit: 1,
  //     },
  //   });
  // }
  const { record, update } = useStoreCurrentRecord(store);
  useBackendCodeEditorTypes();
  const fromDB = isFromDB(store.getCurrentRecord());
  const [showUrl, setShowUrl] = useState(!fromDB);
  const [pending, setPending] = usePending();
  const [validate, setValidate] = useState(false);
  const [result, setResult] = useState(false);
  const platform = usePlatform();
  const isBusy = useStoreBusy(store);
  const [details, setDetails] = useState<ConnectionDetails>({
    username: '',
    password: '',
    host: '',
    port: '',
    dbname: '',
    otherParams: '',
  });

  const enableTestConnection = (): boolean => {
    return (
      !!details.host &&
      !!details.dbname &&
      !!details.username &&
      !!details.password
    );
  };
  useEffect(() => {
    if (!record || record.displayFields === 'N') return;
    const dbType = record.dbType ?? 'MySQL';
    const dbUrl = record.databaseUrl ?? '';
    const isValidUrl = validateUrl(dbType, dbUrl);
    const _databaseUrl = isValidUrl ? dbUrl : '';
    // : SampleConnectionStringMap[dbType];
    const parsed = parseDatabaseUrl(dbType, _databaseUrl);
    setDetails({
      ...parsed,
      password: isValidUrl && parsed.password ? parsed.password : '',
    });
  }, [
    record?.databaseUrl,
    record?.dbType,
    fromDB,
    record,
    record?.displayFields,
  ]);
  if (!record) {
    return <>Missing record! This should never happen...</>;
  }
  //@ts-ignore server validate attach the error log and time taken
  const { error, timeTaken } = record;

  const updateFieldValues = (
    field: 'username' | 'password' | 'host' | 'otherParams' | 'dbname' | 'port',
    value: string,
  ) => {
    const newDetails = {
      ...details,
      [field]: value,
    };
    setDetails(newDetails);
    const newUrl = buildDatabaseUrl(record.dbType ?? 'MySQL', newDetails);
    if (newUrl) update({ databaseUrl: newUrl });
  };

  const isHTML = /<[a-z][\s\S]*>/i.test(error);
  return (
    <div className={classes.root}>
      <form className={classes.form} noValidate autoComplete="off">
        <TextField
          placeholder="e.g. my_app_dev_db"
          autoFocus={!fromDB}
          variant="standard"
          disabled={fromDB}
          fullWidth
          required
          label="Connection Code"
          inputProps={{ 'data-testid': 'connectionCode' }}
          value={record.connectionUid ?? ''}
          onChange={(e) =>
            update({
              connectionUid: e.target.value,
            })
          }
        />
        <TextField
          placeholder="e.g. My Application Development DB"
          variant="standard"
          autoFocus={fromDB}
          fullWidth
          required
          label="Connection Name"
          inputProps={{ 'data-testid': 'connectionName' }}
          value={record.connectionName ?? ''}
          onChange={(e) =>
            update({
              connectionName: e.target.value,
            })
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              data-testid={`displayFields-checkbox-${record.displayFields}`}
              checked={record.displayFields === 'Y'}
              onChange={(e) =>
                update({
                  displayFields: e.target.checked ? 'Y' : 'N',
                })
              }
            />
          }
          label="Use Field-Based Connection"
        />
        <Box component="fieldset" sx={{ borderColor: 'inherit' }}>
          <legend>Connection Details</legend>
          <FormControl variant="standard" fullWidth>
            <InputLabel>Database Type</InputLabel>
            <Select
              inputProps={{ 'data-testid': `dbType-${record.dbType}` }}
              disabled={fromDB}
              value={record.dbType ?? 'MySQL'}
              onChange={(e) =>
                update({
                  dbType: e.target.value as DBType,
                })
              }
            >
              <MenuItem value="MySQL" data-testid="MySQL">
                My SQL
              </MenuItem>
              <MenuItem value="Oracle" data-testid="Oracle">
                Oracle
              </MenuItem>
              <MenuItem value="Postgres" data-testid="Postgres">
                PostgreSQL
              </MenuItem>
              <MenuItem value="MSSQL" data-testid="MSSQL">
                SQL Server
              </MenuItem>
            </Select>
          </FormControl>
          {record.displayFields !== 'Y' ? (
            <TextField
              sx={{
                mt: 2,
                '& .MuiFormHelperText-root': { overflowWrap: 'break-word' },
                maxWidth: width
                  ? width - (record.dbType === 'MSSQL' ? 70 : 60)
                  : undefined,
              }}
              inputProps={{ 'data-testid': 'databaseUrl' }}
              variant="standard"
              fullWidth
              required
              type={showUrl ? 'text' : 'password'}
              label="Database URL"
              value={record.databaseUrl ?? ''}
              onChange={(e) => update({ databaseUrl: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowUrl(!showUrl)}
                      data-tip={showUrl ? 'Hide URL' : 'Show URL'}
                    >
                      <Icon iconName={showUrl ? 'eye-slash' : 'eye'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <>
              <TextField
                fullWidth
                variant="standard"
                label="Host"
                required
                value={details.host}
                onChange={(e) => updateFieldValues('host', e.target.value)}
                sx={{ mt: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  variant="standard"
                  label="Port"
                  required
                  value={
                    // details.port || DEFAULT_PORTS[record.dbType ?? 'MySQL']
                    details.port || ''
                  }
                  onChange={(e) => updateFieldValues('port', e.target.value)}
                  sx={{ mt: 2 }}
                />
                <TextField
                  fullWidth
                  variant="standard"
                  label="Database Name"
                  required
                  value={details.dbname}
                  onChange={(e) => updateFieldValues('dbname', e.target.value)}
                  sx={{ mt: 2 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  variant="standard"
                  label="Username"
                  required
                  value={details.username}
                  onChange={(e) =>
                    updateFieldValues('username', e.target.value)
                  }
                  sx={{ mt: 2 }}
                />
                <TextField
                  fullWidth
                  variant="standard"
                  label="Password"
                  type="password"
                  required
                  value={details.password}
                  onChange={(e) =>
                    updateFieldValues('password', e.target.value)
                  }
                  sx={{ mt: 2 }}
                />
              </Box>
              <TextField
                fullWidth
                variant="standard"
                label="Other Parameters"
                value={details.otherParams || ''}
                onChange={(e) =>
                  updateFieldValues('otherParams', e.target.value)
                }
                sx={{ mt: 2 }}
                helperText={DB_TYPE_HELPER_TEXT[record.dbType ?? 'MySQL']}
              />
            </>
          )}
          <Button
            disabled={
              pending ||
              isBusy ||
              (record.displayFields === 'Y'
                ? !enableTestConnection()
                : !record.databaseUrl)
            }
            sx={{ float: 'right', mt: 2 }}
            variant="contained"
            data-testid="testConnection"
            onClick={() => {
              platform.startAsyncTransition(async () => {
                const re = await store.serverValidate({
                  attribute: 'databaseUrl',
                });
                setResult(re);
                setValidate(true);
              }, setPending);
            }}
            startIcon={
              pending ? (
                <WaveDots active />
              ) : (
                <Icon iconName="anchor" parentColor="primary" />
              )
            }
          >
            Test Connection
          </Button>
        </Box>
        <Alert severity="info">
          <strong>Important: </strong>
          Any reserved characters for URLs (for example, /, :, @, (, ), [, ], &,
          #, =, ?, and space) that appear in any part of the connection URL must
          be percent encoded.
        </Alert>
      </form>
      <div className={classes.header}>
        <Typography className={classes.heading} variant="subtitle1">
          Connection Initialization Script
        </Typography>
      </div>
      <div ref={s.ref} className={classes.editor}>
        <CodeInput
          darkMode={theme.palette.mode !== 'light'}
          height={Math.max(s.height - 24, 140)}
          width={s.width}
          value={record.initScript ?? ''}
          dataTestid={'connection-initialization-script'}
          onChange={(initScript) =>
            update({
              initScript,
            })
          }
          language="typescript"
          allowJSX
        />
      </div>
      <Dialog open={validate}>
        <DialogTitle
          sx={{ pl: 2, fontWeight: 600 }}
          color={
            error || !result
              ? theme.palette.error.dark
              : theme.palette.success.dark
          }
        >
          <IconButton
            sx={{
              mr: 0,
            }}
            size="large"
          >
            <Icon
              color={
                error || !result
                  ? theme.palette.error.dark
                  : theme.palette.success.dark
              }
              iconType="solid"
              iconName={error || !result ? 'times-circle' : 'check-circle'}
            ></Icon>
          </IconButton>
          {error || !result
            ? 'Failed to connect'
            : 'Successfully made connection'}
        </DialogTitle>
        <DialogContent dividers>
          {error || !result ? (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                color={theme.palette.error.dark}
                fontWeight={600}
              >
                Cause
              </Typography>
              {isHTML ? (
                <div dangerouslySetInnerHTML={{ __html: error }} />
              ) : (
                <Typography>{error}</Typography>
              )}
            </Box>
          ) : null}
          <Typography
            fontWeight={600}
            variant="subtitle1"
            gutterBottom
            color={theme.palette.success.dark}
          >
            Connnection Details
          </Typography>
          <Typography>DB Type : {record.dbType}</Typography>
          {record.displayFields === 'Y' ? (
            record?.dbType &&
            record?.databaseUrl && (
              <Typography>
                DB Url: {maskPasswordInUrl(record.dbType, record.databaseUrl)}
              </Typography>
            )
          ) : (
            <Typography>DB Url: {record.databaseUrl}</Typography>
          )}
          {error || !result ? null : (
            <Typography>Time taken to connect : {timeTaken}ms</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            size="medium"
            color="primary"
            onClick={() => setValidate(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const ConnectionEdit = ({
  appUid,
  width,
}: {
  appUid: string;
  width: number;
}): React.ReactElement => {
  const store = useDataStore<IoConnections>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoConnections',
    alias: 'connectionEditAlias',
  });
  const activeConnectionId = useStateXAtomValue(sidebarActiveConnectionUidAtom);
  if (activeConnectionId) {
    store.read({
      props: {
        filter: [
          {
            connectionUid: { is: activeConnectionId },
          },
        ],
        limit: 1,
      },
    });
  }
  return (
    <ConnectionEditBody
      appUid={appUid}
      store={store}
      width={width}
    ></ConnectionEditBody>
  );
};

export default ConnectionEdit;
